// Deterministic color palette for the Jobs → Timeline view.
//
// Each technician (and the "unassigned" pseudo-row) gets a consistent color
// derived from a hash of their id. Same tech → same color every render,
// across sessions, across devices.
//
// Palette is hand-picked for accessibility against the dark luxury bg AND
// the light theme: saturated mid-tones that read against both. Avoid pure
// red/green to keep status semantics free (those are owned by the status
// pills).
const PALETTE = [
  { bar: "#3B82F6", bg: "rgba(59, 130, 246, 0.18)", border: "#3B82F6", chip: "bg-blue-500/15 text-blue-500 border-blue-500/30" },     // blue
  { bar: "#A855F7", bg: "rgba(168, 85, 247, 0.18)", border: "#A855F7", chip: "bg-purple-500/15 text-purple-500 border-purple-500/30" }, // purple
  { bar: "#F97316", bg: "rgba(249, 115, 22, 0.18)", border: "#F97316", chip: "bg-orange-500/15 text-orange-500 border-orange-500/30" }, // orange
  { bar: "#06B6D4", bg: "rgba(6, 182, 212, 0.18)", border: "#06B6D4", chip: "bg-cyan-500/15 text-cyan-500 border-cyan-500/30" },        // cyan
  { bar: "#EC4899", bg: "rgba(236, 72, 153, 0.18)", border: "#EC4899", chip: "bg-pink-500/15 text-pink-500 border-pink-500/30" },       // pink
  { bar: "#10B981", bg: "rgba(16, 185, 129, 0.18)", border: "#10B981", chip: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" }, // emerald
  { bar: "#EAB308", bg: "rgba(234, 179, 8, 0.18)", border: "#EAB308", chip: "bg-yellow-500/15 text-yellow-600 border-yellow-500/30" },   // yellow
  { bar: "#6366F1", bg: "rgba(99, 102, 241, 0.18)", border: "#6366F1", chip: "bg-indigo-500/15 text-indigo-500 border-indigo-500/30" }, // indigo
  { bar: "#14B8A6", bg: "rgba(20, 184, 166, 0.18)", border: "#14B8A6", chip: "bg-teal-500/15 text-teal-500 border-teal-500/30" },       // teal
  { bar: "#F43F5E", bg: "rgba(244, 63, 94, 0.18)", border: "#F43F5E", chip: "bg-rose-500/15 text-rose-500 border-rose-500/30" },        // rose
];

// Slate-ish neutral for the "Unassigned" pseudo-tech row — visually distinct
// from any real tech color so it never collides.
const UNASSIGNED = {
  bar: "#64748B",
  bg: "rgba(100, 116, 139, 0.18)",
  border: "#64748B",
  chip: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i) | 0;
  return Math.abs(h);
}

/** Returns a { bar, bg, border, chip } color object for the given tech id. */
export function getTechColor(techId) {
  if (!techId) return UNASSIGNED;
  return PALETTE[hashStr(String(techId)) % PALETTE.length];
}

/** First letter of each whitespace-separated word, max 2 chars. */
export function techInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

// ── Saved per-company technician colors (Jobs → Map view) ──
// A tech's saved `color` (hex, set from the map legend) wins; otherwise the
// deterministic hash palette above keeps colors consistent with Timeline.

export const TECH_PICKER_PALETTE = [
  "#3B82F6", "#A855F7", "#F97316", "#06B6D4", "#EC4899", "#10B981",
  "#EAB308", "#6366F1", "#14B8A6", "#F43F5E", "#84CC16", "#EF4444",
];

export const UNASSIGNED_COLOR = UNASSIGNED.bar;

/** { techId: hexColor } for every team member (saved color || hash default). */
export function buildTechColorMap(teamMembers = []) {
  const map = {};
  (teamMembers || []).forEach((t) => {
    const id = t.technician_id || t.member_id;
    if (id) map[id] = t.color || getTechColor(id).bar;
  });
  return map;
}
